import { Component, DestroyRef, signal } from '@angular/core';
import { InboxMessageGroup, Message, MessageRecipient, NewMessage, SendMessageReceipients, SendMessageResponse } from '../models/message';
import { CommService } from './comm.service';
import { NzTableFilterList } from 'ng-zorro-antd/table';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NotificationService } from '../shared/notification.service';

@Component({
  selector: 'app-communication',
  templateUrl: './communication.component.html',
  styleUrls: ['./communication.component.less']
})
export class CommunicationComponent {
  
  sentMessages = signal<InboxMessageGroup[]>([]);
  isLoadingSentMessages = false;
  availableRecipients = signal<SendMessageReceipients>({ franchises: [], clients: [], roles: [] });

  isModalVisible = false;
  selectedMessageRecipients = signal<MessageRecipient[]>([]);
  currentPreview: NewMessage | null = null;

  isLoadingRecipients = false;
  isLoadingMessages = false;
  isLoadingAvailableRecipients = false;
  isSending = false;
  selectedTabIndex = 0

  sortByNameFn(a: NewMessage, b: NewMessage): number {
      return a.message.localeCompare(b.message);
    }

    franchiseNames: NzTableFilterList = [];
    userNames: NzTableFilterList = [];
    branchNames: NzTableFilterList = [];
    isViewed: NzTableFilterList = [];

    filterByFranchiseNameFn = (list: string[], item: MessageRecipient): boolean => {
      return list.some(name => item.franchise === name);
    }

    filterByUserNameFn = (list: string[], item: MessageRecipient): boolean => {
      return list.some(name => item.name === name);
    }

    filterByBranchNameFn = (list: string[], item: MessageRecipient): boolean => {
      return list.some(name => item.branch === name);
    }

    filterByIsViewedFn = (list: string[], item: MessageRecipient): boolean => {
      return list.some(filterValue => {
        if (filterValue === 'Viewed') {
          return !!item.dtRead;
        } else if (filterValue === 'Not Viewed') {
          return !item.dtRead;
        }
        return false;
      });
    }

  constructor(public commService: CommService, private sanitizer: DomSanitizer, private notification: NotificationService, private destroyRef: DestroyRef) {}

  ngOnInit() {
    this.isLoadingSentMessages = true;
    const getMessagesSub =this.commService.getMessages().subscribe({
      next: (response) => {
        this.notification.showSuccess('Messages fetched successfully');
        this.sentMessages.set(response);
        this.isLoadingSentMessages = false;
      },
      error: (error) => {
        this.notification.showError('Error occurred while fetching messages:', error);
        this.isLoadingSentMessages = false;
      }
    });
    this.isLoadingAvailableRecipients = true;
    const getAvailableRecipientsSub = this.commService.getAvailableRecipients().subscribe({
      next: (response) => {
          this.availableRecipients.set(response);
          this.isLoadingAvailableRecipients = false;
      },
      error: (error) => {
        this.notification.showError('Error occurred while fetching available recipients:', error);
        this.isLoadingAvailableRecipients = false;
      }
    });

  this.destroyRef.onDestroy(() => {
      getMessagesSub.unsubscribe();
      getAvailableRecipientsSub.unsubscribe();
    });
  }

  public onPreviewChanged(preview: NewMessage): void {
    this.currentPreview = preview;
  }

  public sanitizeHtml(html?: string | null): SafeHtml {
  return this.sanitizer.bypassSecurityTrustHtml(html || '');
}
  public onNewMessage(messageToSend: NewMessage) {
    this.isSending = true;
    this.commService.sendMessage(messageToSend).subscribe({
          next: (response) => {
            this.notification.showSuccess('Message sent successfully');
            this.selectedTabIndex=1;
            this.refreshSentMessages();
            this.isSending = false;
          }
        });
  }

  public getMessagePreview(content: string): string {
    if (!content) return 'No content';
    
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > 80 ? plainText.substring(0, 80) + '...' : plainText;
  }

  public deleteMessage(messageGroupUuid: string): void {
    this.isLoadingSentMessages = true;
    this.commService.deleteMessage(messageGroupUuid).subscribe({
      next: () => {
        this.notification.showSuccess('Message deleted successfully');
        this.refreshSentMessages();
      },
      error: (error) => {
        this.notification.showError('Error occurred while deleting message:', error);
        this.isLoadingSentMessages = false;
      }
    });
  }

  public refreshSentMessages(): void {
    this.isLoadingSentMessages = true;
    this.commService.getMessages().subscribe({
      next: (messages) => {
        this.sentMessages.set(messages);
        this.isLoadingSentMessages = false;
      },
      error: (error) => {
        this.notification.showError('Error occurred while fetching sent messages:', error);
        this.isLoadingSentMessages = false;
      }
    });
  }

  public getCurrentDate(): Date {
    return new Date();
  }

  public getClientName(clientId: number): string {
  try {
    const client = this.availableRecipients().clients.find(c => c.id === clientId);
    return client?.name || `Client ${clientId}`;
  } catch (error) {
    this.notification.showError('Error getting client name:');
    return `Client ${clientId}`;
  }
  }

  public getRoleName(roleId: number): string {
  try {
    const role = this.availableRecipients().roles.find(r => r.id === roleId);
    return role?.name || `Role ${roleId}`;
  } catch (error) {
    this.notification.showError('Error getting role name:');
    return `Role ${roleId}`;
  }
  }

  public getFranchiseName(franchiseId: number): string {
  try {
    const franchise = this.availableRecipients().franchises.find(f => f.id === franchiseId);
    return franchise?.name || `Franchise ${franchiseId}`;
  } catch (error) {
    this.notification.showError('Error getting franchise name:');
    return `Franchise ${franchiseId}`;
  }
  }

  public openMessageRecipients(uuid: string): void {
    this.isLoadingRecipients = true;
    this.commService.getMessageRecipients(uuid).subscribe({
      next: (recipients) => {
        this.selectedMessageRecipients.set(recipients);
        this.franchiseNames = [...new Set(recipients.map(r => r.franchise))]
          .sort()
          .map(name => ({ text: name, value: name }));
        this.userNames = [...new Set(recipients.map(r => r.name))]
          .sort()
          .map(name => ({ text: name, value: name }));
        this.branchNames = [...new Set(recipients.map(r => r.branch))]
          .sort()
          .map(name => ({ text: name, value: name }));
        this.isViewed = [...new Set(recipients.map(r => r.dtRead ? 'Viewed' : 'Not Viewed'))]
          .sort()
          .map(name => ({ text: name, value: name }));
          this.isLoadingRecipients = false;
      },
      error: (error) => {
        this.notification.showError('Error fetching message recipients:', error);
      }
    });
  }

  public openPdf(pdfBase64: string) {
    let pdfWindow = window.open("",'document');
        pdfWindow?.focus();
        pdfWindow?.document.open();
        pdfWindow!.document.write("<title></title><iframe width='100%' height='100%' src='" + pdfBase64 + "'></iframe>");
  }
}
